/** Keep native widgets current after local writes, and at the next day/period boundary. */
import { signOutPending } from "@/lib/sign_out_marker";

import { liveQuery } from "dexie";
import { useEffect, useRef } from "react";

import { readSession } from "@/lib/backend";
import { reportSessionExpired } from "@/lib/sync";
import { cachedFamily } from "@/lib/family";
import { ENTITLEMENT_CHANGED, readCachedEntitlement } from "@/lib/entitlement";
import { FAMILY_REMOVED_KEY } from "@/lib/sync";
import { db, familyDb } from "@/lib/db";
import { bridge } from "@/native/impl";
import { buildWidgetSnapshot } from "@/native/snapshot";

const SNAPSHOT_DEBOUNCE_MS = 150;
const SESSION_CHECK_MS = 30_000;

export function useNativeSnapshot(accountId: string | null): void {
  const identity = useRef({ accountId, generation: 0 });
  if (identity.current.accountId !== accountId) {
    identity.current = { accountId, generation: identity.current.generation + 1 };
  }
  useEffect(() => {
    if (!bridge.hasOutbox) return;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    let boundary: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    const generation = identity.current.generation;
    const current = (): boolean => !disposed && !signOutPending() &&
      (readSession()?.accountId ?? null) === accountId &&
      identity.current.accountId === accountId && identity.current.generation === generation;
    let writes = Promise.resolve();
    const checkSession = (): void => {
      void bridge.sessionGet().then(async (native) => {
        const local = readSession();
        if (!disposed && native !== null && local?.token === native.token && Date.parse(native.expiresAt) <= Date.now()) {
          await reportSessionExpired();
        }
      }).catch((cause: unknown) => { console.warn("Could not check the native session:", cause); });
    };
    const sessionTimer = setInterval(checkSession, SESSION_CHECK_MS);
    document.addEventListener("visibilitychange", checkSession);
    checkSession();
    let latestPublish: (() => void) | undefined;
    const refreshGate = (): void => { latestPublish?.(); };
    window.addEventListener(ENTITLEMENT_CHANGED, refreshGate);
    const query = liveQuery(async () => {
      if (accountId === null) return { profile: null, categories: [], budgets: [], expenses: [] };
      const [profile, categories, budgets, expenses, salaries] = await Promise.all([
        db.profile.get(accountId),
        db.categories.filter((row) => row.userId === accountId).toArray(),
        db.budgets.filter((row) => row.userId === accountId).toArray(),
        db.expenses.filter((row) => row.userId === accountId).toArray(),
        db.salaries.filter((row) => row.userId === accountId).toArray(),
      ]);
      let family;
      let familyUnreadable = 0;
      try {
        const membership = await cachedFamily();
        if (membership && !await familyDb.meta.get(FAMILY_REMOVED_KEY)) {
          const owner = membership.family_id;
          const [sharedProfile, sharedCategories, sharedBudgets, sharedExpenses] = await Promise.all([
            familyDb.profile.get(owner), familyDb.categories.filter(r => r.userId === owner).toArray(),
            familyDb.budgets.filter(r => r.userId === owner).toArray(), familyDb.expenses.filter(r => r.userId === owner).toArray(),
          ]);
          if (sharedProfile) family = { name: membership.name, profile: sharedProfile, categories: sharedCategories, budgets: sharedBudgets, expenses: sharedExpenses, now: new Date() };
        }
      } catch (cause) { familyUnreadable += 1; console.warn("Could not read family widget data:", cause); }
      return { familyUnreadable, profile: profile ?? null, categories, budgets, expenses, salaries, family,
        captureBlocked: readCachedEntitlement(accountId)?.entitlement.captureBlocked ?? false };
    });
    const subscription = query.subscribe({
      next(input) {
        const publish = (): void => {
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            if (!current()) return;
            const entitlement = accountId ? readCachedEntitlement(accountId)?.entitlement : undefined;
            const built = buildWidgetSnapshot({ ...input,
              family: input.family ? { ...input.family, captureBlocked: entitlement?.readOnly || entitlement?.captureBlocked || false } : undefined,
              captureBlocked: entitlement?.captureBlocked ?? false, now: new Date() });
            const snapshot = "unreadable" in built ? { ...built, unreadable: built.unreadable + (input.familyUnreadable ?? 0) } : built;
            writes = writes.then(async () => {
              if (current()) await bridge.writeSnapshot(snapshot, current);
            }).catch((cause: unknown) => { console.warn("Could not update native widgets:", cause); });
            clearTimeout(boundary);
            if ("validUntil" in snapshot) {
              boundary = setTimeout(publish, Math.max(1, Date.parse(snapshot.validUntil) - Date.now()));
            }
          }, SNAPSHOT_DEBOUNCE_MS);
        };
        latestPublish = publish;
        publish();
      },
      error(cause: unknown) { console.warn("Could not read the widget snapshot:", cause); },
    });
    return () => {
      disposed = true;
      window.removeEventListener(ENTITLEMENT_CHANGED, refreshGate);
      clearInterval(sessionTimer);
      document.removeEventListener("visibilitychange", checkSession);
      subscription.unsubscribe();
      clearTimeout(debounce);
      clearTimeout(boundary);
    };
  }, [accountId]);
}
